import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useMemo, useState, useCallback } from "react";

import SAVE_DATOS_CLINICOS_VISITA_CD, { adaptAddVisitaDatosClinicosCDInput } from "../../../../../../data/graphQL/mutations/Visitas/saveVisitaCD";
import { useVisita } from "../../../../../../context/VisitaContext";
import { Grid } from "@mui/material";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useStepSave } from "../../../../hooks/useStepSave";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import CardBox from "../../CardBox/CardBox";
import { SearchDiagnosticoCIE } from "../../../../../ResumenIngresoCreation/components/StepDatosGenerales/components/SearchDiagnosticoCIE";
import { shouldIncludeInput } from "../../../../utils/auxiliaryFunctions";
import { handleChangeSolvedFileVisita } from "../../../../utils/handleChangeImageVisita";
import { tipoArchivoVisita } from "../../../../constants/TipoArchivosVisita";
import { useDeleteArchivoVisita } from "../../../../hooks/useDeleteArchivoVisita";
import { GET_COMBO_VISITA_VALOR, adaptCombosVisitas } from "../../../../../../data/graphQL/queries/MisVisitas/getCombosVisitaValor";
import { GET_COMBO_RI, adaptCombosRIArray } from "../../../../../../data/graphQL/queries/ResumenIngreso/getSingleComboRI";
import { EstudiosComplementarios } from "../../EstudiosComplementarios";
import { GET_ALL_ALERGIAS } from "../../../../../../data/graphQL/queries/ResumenIngreso/getAllAlergias";
import { adaptAlergiaArray } from "../../../../../../adapters/alergiaAdapter";

const ID_COMBO_KARNOFSKY = 7;

export const DatosClinicosCD = ({ readOnly, onSaveRef, onExposeFormatted }) => {
  const [formData, setFormData] = useState({});
  const { visita, setVisita, saveArchivoVisita } = useVisita();
  const [childrenAction, setChildrenAction] = useState(null);
  const [karnofskyOptions, setKarnofskyOptions] = useState([]);
  const [escalaLanskyOptions, setEscalaLanskyOptions] = useState([]);
  const [alergiasOptions, setAlergiasOptions] = useState([]);
  const { deleteArchivo } = useDeleteArchivoVisita();

  const [save] = useMutation(SAVE_DATOS_CLINICOS_VISITA_CD);

  const { data: dataComboVisitaKarn} = useQuery(GET_COMBO_VISITA_VALOR, {
    variables: { idComboVisita: ID_COMBO_KARNOFSKY }
  });

	useEffect(() => {
		if (!dataComboVisitaKarn) return;
    setKarnofskyOptions(adaptCombosVisitas(dataComboVisitaKarn));
	}, [dataComboVisitaKarn]);  

  const { data: dataComboRI } = useQuery(GET_COMBO_RI, {
    variables: { campo: 'ESCALA DE LANSKY ' }
  });

	useEffect(() => {
		if (!dataComboRI) return;
    setEscalaLanskyOptions(adaptCombosRIArray(dataComboRI));
	}, [dataComboRI]);   

  
  const { data: alergiasData } = useQuery(GET_ALL_ALERGIAS, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!alergiasData) return;
    const newAlergias = adaptAlergiaArray(alergiasData);
    setAlergiasOptions(newAlergias);
  }, [alergiasData]);

  const alergiaOtrosId = useMemo(() => {
    const otrosId = alergiasOptions.find((x) => x.displayName.toLowerCase() === 'otros')?.id || -1;
    return otrosId;
  }, [alergiasOptions]);

  useEffect(() => {
    if (!alergiasOptions) return;

    const opcionNo = alergiasOptions.find((x) => x.displayName.includes('NO'));
    const disableCondition = formData.alergiasDatosClinicos?.includes(opcionNo?.id) ?
      (id) => id !== opcionNo?.id :
      (id) => formData.alergiasDatosClinicos?.length ? id === opcionNo?.id : false;

    setAlergiasOptions((prevAlergias) =>
      prevAlergias.map((ant) => ({ ...ant, disabled: disableCondition(ant.id) }))
    );
  }, [formData.alergiasDatosClinicos]);

  const formattedGroups = useMemo(() => {
    return inputsGroups.map((group) => ({
      ...group,
      inputs: group.inputs.
      filter((input) => shouldIncludeInput(input, visita.edad)).
      map((input) => {
        const newInput = { ...input };

        if (newInput.name === "idComboVisitaValorKarnofsky") newInput.options = karnofskyOptions;

        if (newInput.name === "idComboRIValorLansky") newInput.options = escalaLanskyOptions;

        if (newInput.name === 'alergiasDatosClinicos') {
          newInput.options = alergiasOptions;
        }

        if (newInput.name === 'descripcionAlergiaOtro') {
          const hasOtrasAlergias = formData.alergiasDatosClinicos?.includes(alergiaOtrosId);
          newInput.hidden = !hasOtrasAlergias;
          newInput.required = !!hasOtrasAlergias;
        }

        if (readOnly) newInput.disabled = true;

        return newInput;
      })
    }));
  }, [readOnly, visita.edad, karnofskyOptions, escalaLanskyOptions,alergiasOptions, formData.alergiasDatosClinicos]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeFile = useCallback((name, file) => {
    handleChangeSolvedFileVisita({
      saveArchivoVisita,
      name,
      fileValue: file,
      idVisita: visita.idVisita,
      handleChange,
      archivos: formData?.estudioAdjunto,
      idTipoArchivo: tipoArchivoVisita.ESTUDIO_COMPLEMENTARIO
    });
  }, [visita.idVisita, saveArchivoVisita, formData?.estudioAdjunto]);

  const onDeleteImage = useCallback((name, idArchivo, archivos) => {
    deleteArchivo({
      idArchivo: idArchivo,
      name: name,
      archivos: archivos,
      handleChange
    });
  }, [visita.idVisita]);

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedGroups, formData });

    const { data } = await save({
      variables: { input: adaptAddVisitaDatosClinicosCDInput(formData, alergiaOtrosId) }
    });

    const idVisitaCD = data?.createAndUpdateVisitaCD?.idVisitaCD;
    if (!idVisitaCD) throw new Error("Error al guardar");

    setVisita((prev) => ({
      ...prev,
      ...formData,
      idVisitaCD
    }));
  });

  useEffect(() => {
    setFormData(visita);
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "datosClinicosCD",
      stepName: "Datos Clínicos",
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  return (
    <CardBox title="Datos clínicos / Parámetros antropométricos">

            {formattedGroups.
      map((group) =>
      <Grid className='step-form-section-container' container spacing={2}>
                        {
        group.groupName === 'Estudios complementarios' &&
        <label className={`step-form-section-title ${childrenAction ? 'with-children-action' : ''}`}>
                                {group.groupName} {Boolean(childrenAction) && childrenAction}
                            </label>
        }
                        {group.inputs.filter((input) => !input.hidden).
        map((input) =>
        <Grid size={{ xs: 12 }} key={input.name}>
                                    {!['diagnosticoCIE10', 'estudioAdjunto'].includes(input.name) && <RenderInputByTypeMobile
            key={`${group.groupName}-${input.name}`}
            input={input}
            label={input.displayName}
            value={formData[input.name]}
            handleChange={handleChange}
            row={input.type === "multiple_boolean"} />
          }
                                    {input.name === 'diagnosticoCIE10' && <SearchDiagnosticoCIE
            key={`${group.groupName}-${input.name}`}
            diagnosticosRIModel={formData.diagnosticoCIE10}
            handleChange={handleChange}
            inputName={input.name}
            readOnly={readOnly} />
          }
                                    {input.name === 'estudioAdjunto' && <EstudiosComplementarios
            key={`${group.groupName}-${input.name}`}
            name={input.name}
            handleChange={handleChangeFile}
            setChildrenAction={setChildrenAction}
            prevAdjuntos={formData[input.name]}
            onDeleteAdjunto={(name, idArchivo, archivos) => onDeleteImage(name, idArchivo, archivos)}
            readOnly={readOnly} />
          }
                                </Grid>
        )}
                    </Grid>
      )}
        </CardBox>);

};

const inputsGroups = [
{
  groupName: '',
  inputs: [
  {
    name: "diagnosticoCIE10",
    displayName: "Diagnóstico CIE-10",
    type: "custom",
    required: true
  },
  {
    name: "enfermedadOncologica",
    displayName: "Enf. que motiva el soporte es oncológico",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "peso",
    displayName: "Peso actual(Kg)",
    type: "float",
    min: 1,
    max: 350,
    required: true
  },
  {
    name: "talla",
    displayName: "Talla (m)",
    type: "float",
    min: 0,
    max: 3,
    required: true
  },
  {
    displayName: 'Alergias Relevantes',
    name: 'alergiasDatosClinicos',
    type: 'multiple_select',
    options: [],
    required: true
  },
  {
    displayName: 'Otras alergias',
    name: 'descripcionAlergiaOtro',
    type: 'long_string',
    required: true
  },
  {
    name: "soporteNutricional",
    displayName: "Requiere soporte nutricional",
    type: "boolean",
    lightTitle: true,
    required: true
  }]

},
{
  groupName: 'Estudios complementarios',
  inputs: [
  {
    displayName: 'Estudio complementario',
    name: 'estudioAdjunto'
  }]

},
{
  groupName: '',
  inputs: [
  {
    name: "examenFisico",
    displayName: "Examen físico(Datos positivos)",
    type: "long_string",
    required: true
  },
  {
    name: "traqueostomia",
    displayName: "Traqueostomía",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "sondaVesical",
    displayName: "Sonda vesical",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "ostomia",
    displayName: "Ostomía",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "cateter",
    displayName: "Catéter",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "sondaNasogastrica",
    displayName: "Sonda nasogástrica",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "idComboVisitaValorKarnofsky",
    displayName: "Karnofsky",
    type: "select",
    esAdulto: true,
    required: true
  },
  {
    name: "idComboRIValorLansky",
    displayName: "Escala LANSKY",
    type: "select",
    esPediatrico: true,
    required: true
  }]

}];